// Lógica pura da tela Horários e jornada (análise).
// buildRequestBody/buildDashboardBody: estado da tela -> corpo camelCase do BFF.
// build*VM: DTOs de dashboard/risco do BFF -> view-models dos gráficos (barras).
// O BFF já devolve os itens na ordem de exibição; aqui só traduzimos key -> rótulo/cor.

export interface HorarioTableFiltros {
  cnpjList: string[];
  step: string[];
  status: string[];
  classification: string[];
  risk: string[];
  // ---- CON-5227: filtros restaurados da listagem ----
  occupationCode: string[]; // ocupação (ci.code)
  cargo: string[];
  categoria: number[];
  tipoRegistro: string[]; // regime trabalhista (t.tpregtrab_code)
  regimePrevidenciario: number[];
  tipoJornada: number[];
  cargaHorariaSemanal: number[];
  horarioNoturno: boolean | null;
  // "Tempo parcial" NÃO é boolean no modelo de jornada: é FK part_time_contract_id -> descrição.
  tempoParcial: number[];
  versao: string[];
  declaracao: string[];
  // Envio do evento (teh.register_date) — intervalo de data (yyyy-mm-dd).
  envioInicio: string | null;
  envioFim: string | null;
}

export interface HorarioState {
  cnpjList: string[]; // empregador selecionado
  busca: string; // busca da tabela (filterTerm)
  filtros: HorarioTableFiltros;
}

export interface HorarioRequestBody {
  cnpjList: string[];
  step: string[];
  status: string[];
  classification: string[];
  risk: string[];
  filterTerm?: string;
  occupationCode: string[];
  cargo: string[];
  categoria: number[];
  tipoRegistro: string[];
  regimePrevidenciario: number[];
  tipoJornada: number[];
  cargaHorariaSemanal: number[];
  horarioNoturno?: boolean;
  tempoParcial: number[];
  versao: string[];
  declaracao: string[];
  envioInicio?: string;
  envioFim?: string;
}

// Corpo dos gráficos: só o filtro geral (Empregador) — paridade com o legado.
export function buildDashboardBody(cnpjList: string[]): { cnpjList: string[] } {
  return { cnpjList };
}

export function buildRequestBody(s: HorarioState): HorarioRequestBody {
  const term = s.busca.trim();
  return {
    cnpjList: s.filtros.cnpjList.length > 0 ? s.filtros.cnpjList : s.cnpjList,
    step: s.filtros.step,
    status: s.filtros.status,
    classification: s.filtros.classification,
    risk: s.filtros.risk,
    filterTerm: term || undefined,
    occupationCode: s.filtros.occupationCode,
    cargo: s.filtros.cargo,
    categoria: s.filtros.categoria,
    tipoRegistro: s.filtros.tipoRegistro,
    regimePrevidenciario: s.filtros.regimePrevidenciario,
    tipoJornada: s.filtros.tipoJornada,
    cargaHorariaSemanal: s.filtros.cargaHorariaSemanal,
    horarioNoturno: s.filtros.horarioNoturno ?? undefined,
    tempoParcial: s.filtros.tempoParcial,
    versao: s.filtros.versao,
    declaracao: s.filtros.declaracao,
    envioInicio: s.filtros.envioInicio || undefined,
    envioFim: s.filtros.envioFim || undefined,
  };
}

// Options de filtro (CON-5227) — valores distintos existentes no banco (POST .../filtros).
export interface JornadaFiltroOpcaoItem {
  value: string;
  label: string;
}

export interface JornadaFiltrosOpcoes {
  ocupacao: JornadaFiltroOpcaoItem[];
  cargo: string[];
  categoria: JornadaFiltroOpcaoItem[];
  tipoRegistro: string[];
  regimePrevidenciario: JornadaFiltroOpcaoItem[];
  tipoJornada: JornadaFiltroOpcaoItem[];
  cargaHorariaSemanal: string[];
  tempoParcial: JornadaFiltroOpcaoItem[];
  versao: string[];
  declaracao: string[];
}

export const FILTROS_OPCOES_VAZIO: JornadaFiltrosOpcoes = {
  ocupacao: [],
  cargo: [],
  categoria: [],
  tipoRegistro: [],
  regimePrevidenciario: [],
  tipoJornada: [],
  cargaHorariaSemanal: [],
  tempoParcial: [],
  versao: [],
  declaracao: [],
};

// ---- rótulo + cor por enum (espelham a legenda do gráfico do legado) ----

interface Meta {
  label: string;
  cor: string;
}

// Etapas de análise — ordem e cores padronizadas com o gráfico do CBO (cbo.ts COR_STEP).
const STEP_META: Record<string, Meta> = {
  ANALISAR: { label: 'Analisar', cor: '#868E96' },
  PENDENTE_DE_ESCLARECIMENTO: { label: 'Esclarecimento', cor: '#E4C23F' },
  EM_ANALISE: { label: 'Em análise', cor: '#4263EB' },
  APTO_PARA_REVISAO: { label: 'Apto para revisão', cor: '#12B886' },
  REVISADO: { label: 'Revisado', cor: '#7048E8' },
  ANALISADO: { label: 'Analisado', cor: '#2F9E44' },
};
const STEP_ORDER = [
  'ANALISAR',
  'PENDENTE_DE_ESCLARECIMENTO',
  'EM_ANALISE',
  'APTO_PARA_REVISAO',
  'REVISADO',
  'ANALISADO',
];

// Status da análise
const STATUS_META: Record<string, Meta> = {
  PENDENTE_DE_ESCLARECIMENTO: { label: 'Esclarecimento', cor: '#D6336C' },
  EM_ANALISE: { label: 'Em análise', cor: '#9CA3AF' },
  ANALISADO: { label: 'Analisado', cor: '#3B2C8F' },
};
const STATUS_ORDER = ['PENDENTE_DE_ESCLARECIMENTO', 'EM_ANALISE', 'ANALISADO'];

// Classificação de análise
const CLASSIFICATION_META: Record<string, Meta> = {
  EM_ANALISE: { label: 'Em análise', cor: '#9CA3AF' },
  PENDENTE_DE_ESCLARECIMENTO: { label: 'Esclarecimento', cor: '#D6336C' },
  RISCO_DETECTADO: { label: 'Analisado - Com Risco', cor: '#9B2C2C' },
  INEXPRESSIVO: { label: 'Analisado - Correta', cor: '#3B5BDB' },
};
const CLASSIFICATION_ORDER = [
  'EM_ANALISE',
  'PENDENTE_DE_ESCLARECIMENTO',
  'RISCO_DETECTADO',
  'INEXPRESSIVO',
];

// Exposição ao risco
const RISK_META: Record<string, Meta> = {
  CRITICO: { label: 'Crítico', cor: '#E03131' },
  ELEVADO: { label: 'Elevado', cor: '#F08C00' },
  MODERADO: { label: 'Moderado', cor: '#E4C23F' },
  BAIXO: { label: 'Baixo', cor: '#2F9E44' },
  INEXPRESSIVO: { label: 'Inexpressivo', cor: '#8189A6' },
};
const RISK_ORDER = ['CRITICO', 'ELEVADO', 'MODERADO', 'BAIXO', 'INEXPRESSIVO'];

// rótulos crus (tradução das colunas etapa/status/classificação/risco da tabela)
export const STEP_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(STEP_META).map(([k, v]) => [k, v.label]),
);
export const STATUS_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(STATUS_META).map(([k, v]) => [k, v.label]),
);
export const CLASSIFICATION_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(CLASSIFICATION_META).map(([k, v]) => [k, v.label]),
);
export const RISK_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(RISK_META).map(([k, v]) => [k, v.label]),
);

// ---- DTOs (espelham os responses do BFF) ----

export interface DashboardItemDTO {
  key: string;
  quantity: number;
  percentage: number;
}

export interface JornadaDashboardDTO {
  step: DashboardItemDTO[];
  status: DashboardItemDTO[];
  classification: DashboardItemDTO[];
}

export interface RiskChartItemDTO {
  risk: string;
  quantity: number;
  percentage: number;
}

export interface JornadaRiscoDTO {
  totalItems: number;
  items: RiskChartItemDTO[];
}

export interface Barra {
  key: string; // enum de origem (estável; usado em data-qa)
  label: string;
  count: number;
  valor: number; // percentual 0-100
  cor: string;
}

function toBarras(
  items: DashboardItemDTO[] | undefined,
  order: string[],
  meta: Record<string, Meta>,
): Barra[] {
  const byKey = new Map((items ?? []).map((i) => [i.key, i]));
  return order.map((key) => {
    const item = byKey.get(key);
    const m = meta[key];
    return {
      key,
      label: m.label,
      count: item?.quantity ?? 0,
      valor: item?.percentage ?? 0,
      cor: m.cor,
    };
  });
}

export function buildStepVM(d?: JornadaDashboardDTO): Barra[] {
  return toBarras(d?.step, STEP_ORDER, STEP_META);
}

export function buildStatusVM(d?: JornadaDashboardDTO): Barra[] {
  return toBarras(d?.status, STATUS_ORDER, STATUS_META);
}

export function buildClassificationVM(d?: JornadaDashboardDTO): Barra[] {
  return toBarras(d?.classification, CLASSIFICATION_ORDER, CLASSIFICATION_META);
}

export function buildRiskVM(d?: JornadaRiscoDTO): Barra[] {
  const items: DashboardItemDTO[] = (d?.items ?? []).map((i) => ({
    key: i.risk,
    quantity: i.quantity,
    percentage: i.percentage,
  }));
  return toBarras(items, RISK_ORDER, RISK_META);
}
